import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal, PortalModule } from '@angular/cdk/portal';
import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { cloneDeep } from 'lodash-es';
import { ButtonDirective, IconDirective, SpinnerComponent, TooltipDirective } from 'shared/ui-common';

import { ComponentDto, ProposedComponentDto } from '@treetop/shared/model';

import { MedicalComponentSearchComponentService } from './medical-component-search.component.service';
import { FilterOptions } from './model/filter-options';
import { SearchResultDto } from './model/search-result-dto';
import { SearchFilterComponent } from './search-filter/search-filter.component';

@Component({
  standalone: true,
  selector: 'shared-medical-component-search',
  templateUrl: './medical-component-search.component.html',
  styleUrls: ['./medical-component-search.component.scss'],
  imports: [
    AsyncPipe,
    MatInputModule,
    ReactiveFormsModule,
    MatTooltipModule,
    PortalModule,
    SearchFilterComponent,
    TranslocoModule,
    SpinnerComponent,
    MatIconModule,
    IconDirective,
    TooltipDirective,
    ButtonDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MedicalComponentSearchComponentService],
})
export class MedicalComponentSearchComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild(CdkPortal) autocompleteOverlay!: CdkPortal;
  @ViewChild('searchInput') searchInput: ElementRef;

  @Input() public filterOptions: FilterOptions;
  @Input() isDialog: boolean;

  //add extra space for rule icon for popover
  @Input() addOffset?: boolean;

  @Output() selectedComponent = new EventEmitter<SearchResultDto>();
  @Output() searchInputBlur = new EventEmitter<void>();

  protected loading = signal(false);
  protected minSearchLength = 3;

  protected searchText = new FormControl('');

  private overlay = inject(Overlay);
  private overlayRef: OverlayRef | null;

  protected service = inject(MedicalComponentSearchComponentService);
  protected elementRef = inject(ElementRef);

  protected isOverlayOpen = signal(false);

  constructor() {
    this.searchText.valueChanges.subscribe((searchText: string) => {
      this.service.updateFilter({ searchText });
    });
  }

  public ngOnChanges({ filterOptions }: SimpleChanges) {
    this.service.updateFilter({ filter: cloneDeep(filterOptions.currentValue) });
  }

  public ngAfterViewInit() {
    if (!this.isDialog) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      });
    }
  }

  protected filterChanged(filter: FilterOptions): void {
    this.service.updateFilter({ filter });
  }

  protected addProposedComponent(): void {
    const newComponentText = this.searchText.value;
    if (newComponentText === '') {
      return;
    }

    const result: SearchResultDto = { newComponentText };
    this.selectedComponent.emit(result);
  }

  protected componentSelected(component: ComponentDto | ProposedComponentDto, proposed = false): void {
    if (component.code.startsWith('PC-') || proposed) {
      const result: SearchResultDto = { proposedComponentDto: component };
      this.selectedComponent.emit(result);
    } else {
      const result: SearchResultDto = { componentDto: component };
      this.selectedComponent.emit(result);
    }
  }

  protected handleInputFocus(): void {
    this.openOverlay();
  }

  protected onEscape(): void {
    this.searchInputBlur.emit();
  }

  protected openOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    this.isOverlayOpen.set(true);

    this.overlayRef = this.overlay.create(this.getOverlayConfig());
    this.overlayRef.attach(this.autocompleteOverlay);

    this.overlayRef.outsidePointerEvents().subscribe((event) => {
      const isClickOutside = !this.elementRef.nativeElement.contains(event.target);
      if (isClickOutside) {
        this.closeOverlay();
        this.searchInputBlur.emit();
      }
    });
  }

  private getOverlayConfig(): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.searchInput.nativeElement)
      .withFlexibleDimensions(true)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);

    return {
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this.searchInput.nativeElement.offsetWidth,
      hasBackdrop: false,
    };
  }

  private closeOverlay(): void {
    if (this.overlayRef) {
      this.isOverlayOpen.set(false);
      this.overlayRef.detach();
      this.overlayRef = null;
    }
  }

  public ngOnDestroy(): void {
    this.closeOverlay();
  }

  protected readonly Object = Object;
}
